import { Dialog, Transition } from "@headlessui/react";
import { BackwardIcon, ForwardIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { Fragment, useRef, useState, useEffect } from "react";

export default function ProductItemImage({ value, data }) {
  const [open, setOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageUrls, setImageUrls] = useState<Record<number, string>>({}); // store fixed blob URLs

  const handleOpen = () => {
    const itemIndex = data?.findIndex(
      (item) => item?.vetic_variant_id === value?.vetic_variant_id
    );
    setCurrentImageIndex(itemIndex);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setImageUrls({})
    setCurrentImageIndex(0)
  };

  // Determine if the "Prev" button should be disabled
  const isPrevButtonDisabled = currentImageIndex === 0;

  // Determine if the "Next" button should be disabled
  const isNextButtonDisabled = currentImageIndex === data?.length - 1;

  // Carousel functionality
  const nextImage = () => {
    if (!isNextButtonDisabled) {
      setCurrentImageIndex((prevIndex) => prevIndex + 1);
    }
  };

  const prevImage = () => {
    if (!isPrevButtonDisabled) {
      setCurrentImageIndex((prevIndex) => prevIndex - 1);
    }
  };
  useEffect(() => {
    const fetchImage = async () => {
      const currentItem = data?.[currentImageIndex];
      if (!currentItem || imageUrls[currentImageIndex]) return;

      try {
        const response = await fetch(currentItem.thumbnail, { mode: "cors" });

        if (!response.ok) throw new Error("Fetch failed");

        const arrayBuffer = await response.arrayBuffer();
        const blob = new Blob([arrayBuffer], { type: "image/jpeg" });

        // If blob has size 0, fallback
        if (blob.size === 0) throw new Error("Empty blob");

        const url = URL.createObjectURL(blob);
        setImageUrls((prev) => ({ ...prev, [currentImageIndex]: url }));
      } catch (error) {
        console.error("Error loading image, falling back to original:", error);
        // fallback to original URL
        setImageUrls((prev) => ({ ...prev, [currentImageIndex]: currentItem.thumbnail }));
      }
    };

    if (open) {
      fetchImage();
    }
  }, [currentImageIndex, open, data, imageUrls]);

  // cleanup only when modal closes or unmounts
  useEffect(() => {
    if (!open) {
      return () => {
        Object.values(imageUrls).forEach((url) => {
          if (url.startsWith("blob:")) {
            URL.revokeObjectURL(url);
          }
        });
      };
    }
  }, [open, imageUrls]);


  const cancelButtonRef = useRef(null);

  return (
    <div>
      <span onClick={handleOpen} className="text-xs cursor-pointer">
        <span className="text-gray-900">{value?.item_name}</span>
        <span className="ml-2 inline-flex items-center rounded-full border border-[#D6E2FB] bg-[#F1F5FF] px-2 py-0.5 leading-[14px] text-[10px] font-semibold text-[#4C6FA8] whitespace-nowrap">
          {value?.quantity_to_pick} Qty
        </span>
      </span>

      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-40"
          initialFocus={cancelButtonRef}
          onClose={handleClose}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-4 sm:w-full sm:max-w-md sm:p-6">
                  <div>
                    <div className="flex justify-end">
                      <span className="cursor-pointer" onClick={handleClose}>
                        <XMarkIcon className="h-4 w-4" />
                      </span>
                    </div>
                    <div className="h-auto flex justify-center flex-col items-center gap-2">
                      <div className="h-[10rem] w-[10rem] flex items-center justify-center">
                        {imageUrls[currentImageIndex] ? (
                          <img
                            src={imageUrls[currentImageIndex]}
                            alt={data[currentImageIndex]?.item_name}
                            className="h-auto w-auto max-h-full max-w-full object-contain"
                          />
                        ) : (
                          <p className="text-gray-400">Loading image...</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <button
                          className="cursor-pointer disabled:opacity-40"
                          onClick={prevImage}
                          disabled={isPrevButtonDisabled}
                        >
                          <BackwardIcon className="w-4 h-4" />
                        </button>
                        <button
                          className="cursor-pointer disabled:opacity-40"
                          onClick={nextImage}
                          disabled={isNextButtonDisabled}
                        >
                          <ForwardIcon className="h-4 w-4" />
                        </button>
                      </div>
                      <p className="mt-2">
                        {data[currentImageIndex]?.item_name}{" "}
                        <span className="font-bold">
                          - {data[currentImageIndex]?.quantity_to_pick} Qty
                        </span>
                      </p>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
}
